(ns web3.modals.core
  (:require [re-frame.db :refer [app-db]]
            [goog.dom.classes :as classes]
            [goog.events :as events]
            [web3.view :as v]
            [reagent.core :as r]
            [web3.components.h-slider :as h-slider]
            [web3.utils :as utils]
            [web3.ui-state :as state]
            [web3.icons.shapes :as shapes]
            [web3.view.hiccup :as hiccup]
            [web3.ui-state :as ui-state]))

(v/defview modal-container
  {:component-did-mount (fn [this]
                          (swap! (:state this) assoc :mounted? true))}
  [{:keys [id key class on-close z-index web-only?]
    :or {class ""
         web-only? false}} content]
  (let [this (r/current-component)
        {:keys [mounted?]} @(:state this)]
    [:div.flex.items-center.justify-center
     {:id id
      :key key
      :class (cond-> (str class
                          (when-not (ui-state/mobile?) " modal-container")
                          (when web-only? " relative"))
               (and content mounted?) (str " modal--bg"))
      :style {:z-index z-index}
      :on-click #(let [target (.-target %)]
                   (when (and (some? on-close)
                              (or (= target (.-currentTarget %))
                                  (classes/has target "modal--close-target")
                                  (utils/closest target (fn [el]
                                                          (classes/has el "modal--close")))))

                     (on-close)))}
     content]))

(defn modal-buttons [buttons]
  [:div.flex
   {:style {:margin-right -1}
    :class (when (> (count buttons) 2)
             " flex-column")}
   (for [{:keys [href label color] :as props} buttons]
     [(if href :a :div)
      (-> props
          (assoc :key label)
          (assoc-in [:style :margin-right] "1px")
          (update :class str
                  " bg-" (or color "black hover-bg-black-hover")
                  " flex items-center justify-center text-white text-button pad-all-3 flex-auto cursor-pointer")
          (dissoc :label :color))
      label])])

(defn delete-button
  [props title]
  [:div.border-light-gray.text-uppercase.round-3.pad-all-2_5.weight-bold.text-size-14.text-center.hover-bg-off-white.cursor-pointer
   props
   title])

(defn header-link
  ([content] (header-link {} content))
  ([props content]
   [:a.text-dark-gray.hover-text-black-hover.flex.items-center.pad-all-3
    props
    content]))

(defn back-button
  [back-href]
  [header-link
   {:href back-href
    :style {:opacity (if back-href 1 0)
            :transition "opacity 0.2s ease"}}
   (-> shapes/chevron-left
       (hiccup/size 18))])

(defn header
  [{:keys [title
           back-href
           right-action
           after
           modal/closable?]
    :or {closable? true}}]
  [:div.modal-header.width-100p.modal-rounded-top.bg-white.relative.height-auto
   {:key "modal-header"}
   [:div.flex.items-stretch
    [:div.absolute.left-0.top-0
     [back-button back-href]]
    [:div.width-100p.text-center.text-category.pad-all-3
     title]
    [:div.absolute.right-0.top-0
     (when right-action
       right-action)
     (when closable?
       [:div.pad-all-3.cursor-pointer.modal--close.text-dark-gray
        shapes/close-x])]]
   after])


(def max-width 375)

(defn nav-link [{:keys [href on-click side] :as props} icon]
  [:div.flex.items-center.absolute.justify-center
   (v/merge-props (dissoc props :href :side)
                  {:class (case side
                            :left "lightbox-link left"
                            :right "lightbox-link right")})
   [:a.pad-v-3.text-white.flex.width-100p
    {:href href
     :class (case side :left "justify-start pad-left-2_5"
                       :right "justify-end pad-right-2_5")}
    icon]])

(defn close! [{:as component
               :keys [state]
               {:keys [on-close]} :props}]
  (when (and (some? on-close)
             (on-close))
    (swap! state assoc :transition :will-unmount)))

(defn modal-nav [{:keys [:z-index
                         :wide?
                         :link-prev
                         :link-next
                         :direct-close!]}]
  [:div.fixed.mobile-hidden
   {:style {:left 0
            :right 0
            :top 150
            :z-index z-index}}
   [:div.margin-h-auto {:class (if wide?
                                 "modal-wide"
                                 "modal-narrow")}
    (when link-prev
      [nav-link {:href link-prev
                 :side :left
                 :on-click direct-close!} shapes/chevron-left])

    (when link-next
      [nav-link {:href link-next
                 :side :right
                 :on-click direct-close!} shapes/chevron-right])]])

(v/defview lightbox
  {:get-initial-state
   (fn [this]
     {:transition :will-mount})
   :component-did-mount
   (fn [{:keys [state] :as this}]
     (some->
      ^js (r/dom-node this)
      (.getElementsByClassName "web-overflow-y-auto")
      (aget 0)
      (.addEventListener
       "scroll"
       ;; on scroll, write the :lightbox/scroll-y property.
       ;; the autocomplete component needs this so that it updates
       ;; location when a lightbox scrolls. (otherwise the form can move,
       ;; while autocomplete remains in place)
       (fn [e]
         (swap! state/ui-globals assoc :lightbox/scroll-y (.-scrollTop (.-target e))))))

     ;; listen for escape key, to close modal
     (let [listener-key (events/listen js/window "keydown"
                                       (fn [^js e]
                                         (when (= 27 (.-keyCode e))
                                           (close! this))))]
       (v/on-unmount! this :esc-listener #(events/unlistenByKey listener-key)))

     (swap! state assoc
            :transition :mounted))
   :component-will-receive-props
   v/set-prev-props!
   :component-will-unmount
   (fn [{:keys [state]}]
     (some-> (:keydown-cb @state)
             (events/unlistenByKey)))}
  [{:as props
    :keys [id ids link-fn wide? on-close z-index]}
   & content]
  (let [{:keys [state] :as this} (r/current-component)
        direct-close! #(when (= (.-target %) (.-currentTarget %))
                         (close! this))
        {:keys [transition]} @state
        ids-before (take-while #(not= id %) ids)
        index (count ids-before)
        ids-after (drop (inc index) ids)
        link-prev (some-> ids-before
                          last
                          (link-fn))
        link-next (some-> ids-after
                          first
                          (link-fn))
        wrap (if link-fn
               (fn [content]
                 [h-slider/h-slider
                  {:key "lightbox-slider"
                   :index index
                   :content/key id
                   :content content}])
               identity)]
    [modal-container
     {:id id
      :on-close on-close
      :z-index (dec z-index)
      :web-only? true}
     [:div.web-overflow-y-auto.height-100p.width-100p.relative
      {:key "lightbox"
       :on-click direct-close!
       :style {:z-index z-index}}
      (wrap
       [:div.flex.flex-column.items-center
        {:key id
         :on-click direct-close!}
        [:div.shadow-modal.relative.modal-rounded.flex-column.flex
         (let [min-height
               (let [mobile-height 667
                     vertical-padding 64
                     {:keys [window/inner-height
                             window/inner-width]} @state/ui-globals]
                 (if (<= inner-width 700)
                   nil
                   (if (< inner-height
                          mobile-height)
                     "80vh"
                     (- mobile-height vertical-padding))))]
           (-> {:class (if wide?
                         "modal-wide"
                         "modal-narrow")
                :style {:transition "0.15s ease all"
                        :transform (str "scale(" (case transition
                                                   (:will-mount :will-unmount) 0.8
                                                   :mounted 1) ")")
                        :min-height (get props :min-height min-height)
                        :opacity (case transition
                                   (:will-mount :will-unmount) 0
                                   :mounted 1)}}
               (v/merge-props (dissoc props
                                      :min-height
                                      :id
                                      :ids
                                      :link-fn
                                      :content
                                      :wide?
                                      :on-close
                                      :z-index))))

         [modal-nav {:z-index z-index
                     :wide? wide?
                     :link-prev link-prev
                     :link-next link-next
                     :direct-close! direct-close!}]
         (into [:div.flex.flex-column.modal-content.flex-auto
                {:style {:z-index (inc z-index)}}]
               content)]])]]))
